import Donut from './Donut.jsx';
import { money, pct } from './format.js';

const POOL_COLORS = {
  taxDeferred: '#1a2040',
  // Green belongs to the money that is never taxed again; blue is the working
  // capital. The ramp is an argument, not a decoration.
  taxFree: '#1a7a4a',
  taxable: '#015697',
};

const POOL_BLURB = {
  taxDeferred: 'Taxed as ordinary income when withdrawn',
  taxFree: 'No tax on qualified withdrawals',
  taxable: 'Capital gains treatment, available any time',
};

// The tax triangle's fine print, from the advisor lead's deck: what each
// treatment gives and what it takes. Static on purpose; these are facts about
// the tax code, not about the client, and an advisor should be able to recite
// them without checking whether the software agrees.
const POOL_TERMS = {
  taxable: {
    pros: ['Capital gains rates, 0 to 20%', 'Losses can offset gains', 'Heirs receive a step-up in basis'],
    cons: ['Interest and dividends taxed every year'],
  },
  taxDeferred: {
    pros: ['Deduction now, growth untaxed until withdrawal'],
    cons: ['Withdrawals taxed as ordinary income, up to 37%', 'Required distributions at 73 to 75', 'Heirs pay income tax on what they inherit'],
  },
  taxFree: {
    pros: ['Qualified withdrawals never taxed', 'No required distributions', 'Heirs receive it income-tax free'],
    cons: ['Contribution limits and income restrictions'],
  },
};

export default function TaxPoolsSection({ snapshot, bundle }) {
  if (!snapshot || !snapshot.total) return null;

  const segments = snapshot.pools
    .filter(p => p.value > 0)
    .map(p => ({ label: p.label, value: p.value, color: POOL_COLORS[p.key] }));

  const deferred = snapshot.pools.find(p => p.key === 'taxDeferred');

  return (
    <section className="asmt-card" id="tax-pools">
      <div className="asmt-card-head">
        <h2>Where your money sits today</h2>
        <span className="asmt-card-note">{money(snapshot.total)} in liquid assets</span>
      </div>

      <p className="asmt-lede">
        Every dollar you have saved falls into one of three tax treatments, and
        which one it sits in decides how much of it you actually get to spend.
      </p>

      <Donut segments={segments} centerValue={money(snapshot.total)} centerLabel="Liquid" />

      <div className="asmt-pools">
        {snapshot.pools.map(pool => {
          const share = snapshot.total > 0 ? (pool.value / snapshot.total) * 100 : 0;
          const poolAllocation = bundle?.pools?.[pool.key]?.allocation;
          return (
            <div className="asmt-pool" key={pool.key}>
              <div className="asmt-pool-label">{pool.label}</div>
              <div className="asmt-pool-value">{money(pool.value)}</div>
              <div className="asmt-pool-share">{pct(share, 0)} of liquid assets</div>
              <div className="asmt-bar-track" style={{ marginTop: 10 }}>
                <div
                  className="asmt-bar-fill"
                  style={{ width: `${share}%`, background: POOL_COLORS[pool.key] }}
                />
              </div>
              <div className="asmt-pool-share" style={{ marginTop: 8 }}>{POOL_BLURB[pool.key]}</div>
              <ul className="asmt-pool-terms">
                {POOL_TERMS[pool.key].pros.map(t => (
                  <li className="is-pro" key={t}>{t}</li>
                ))}
                {POOL_TERMS[pool.key].cons.map(t => (
                  <li className="is-con" key={t}>{t}</li>
                ))}
              </ul>

              {pool.rows.length > 0 && (
                <div className="asmt-pool-rows">
                  {pool.rows.slice(0, 4).map((row, i) => (
                    <div className="asmt-pool-row" key={i}>
                      <span>{row.label}{row.type ? ` · ${row.type}` : ''}</span>
                      <span>{money(row.value)}</span>
                    </div>
                  ))}
                  {pool.rows.length > 4 && (
                    <div className="asmt-pool-row asmt-muted">
                      <span>{pool.rows.length - 4} more</span>
                      <span />
                    </div>
                  )}
                </div>
              )}

              {/* Only present once statements have been analyzed: the mix
                  inside each pool is what the holdings data adds over the
                  questionnaire's account balances. */}
              {poolAllocation && (
                <div className="asmt-pool-rows">
                  {Object.entries(poolAllocation)
                    .filter(([, v]) => v > 0)
                    .map(([klass, v]) => (
                      <div className="asmt-pool-row" key={klass}>
                        <span>{klass}</span>
                        <span>{pct(v, 0)}</span>
                      </div>
                    ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {deferred && snapshot.deferredSharePct >= 50 && (
        <div className="asmt-callout">
          <strong>{pct(snapshot.deferredSharePct, 0)} of your liquid savings has never been taxed.</strong>{' '}
          Money in that column is taxed as ordinary income as you withdraw it, and
          required minimum distributions eventually force the timing. Deciding
          when those dollars come out, rather than being told when, is usually
          the single largest lever available.
        </div>
      )}
    </section>
  );
}
